import { ReactNode, useState, useEffect, useRef, useCallback, CSSProperties } from 'react';
import close from '@/assets/icons/close-white.svg';
import styles from './Blade.module.scss';

interface BladeProps {
  handleXclick: () => void;
  headerTop: ReactNode;
  headerBottom: ReactNode;
  children: ReactNode;
  width: string;
}

/**
 * Clicks that open a blade rather than leave it: table rows, and any control marked
 * `data-opens-blade` (HAT-1395: the request numbers in a board notice - outside the table, their
 * click opened the drawer and the same click, reaching the document, closed it again).
 */
const TABLE_ROW_SELECTOR = '[data-table-row], tbody tr:not([aria-hidden]), [data-opens-blade]';

/**
 * ⚠️ נקרא ממסלול האירוע, ולא מ-event.target כפי שהוא ברגע שהאירוע מגיע ל-document.
 *
 * ה-onClick של השורה רץ ראשון, מרנדר מחדש את הלוח, ו-React מחליף את התא שנלחץ
 * (כל cell renderer ב-DataTable הוא פונקציה חדשה בכל רינדור, ולכן הוא נחשב טיפוס
 * אחר ומורכב מחדש). כשהלחיצה מגיעה לכאן ה-target כבר מנותק מהמסמך, closest אינו
 * מוצא שורה, והמגירה שאותה לחיצה בדיוק פתחה נסגרה מייד (HAT-1324).
 *
 * composedPath נקבע בתחילת ה-dispatch ולכן הוא עדיין מחזיק את השורה עצמה.
 */
const eventPath = (event: MouseEvent): Element[] =>
  event.composedPath().filter((node): node is Element => node instanceof Element);

const isTableRowTarget = (path: Element[]) => path.some((element) => element.matches(TABLE_ROW_SELECTOR));

const Blade = ({ handleXclick, headerTop, headerBottom, children, width }: BladeProps) => {
  const [isBladeHidden, setIsBladeHidden] = useState(true);
  const bladeRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelPendingClose = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const closeBlade = useCallback(() => {
    cancelPendingClose();
    setIsBladeHidden(true);
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      handleXclick();
    }, 300); // Transition Delay
  }, [cancelPendingClose, handleXclick]);

  const reopenBlade = useCallback(() => {
    cancelPendingClose();
    setIsBladeHidden(false);
  }, [cancelPendingClose]);

  const handleWheel = (e: React.WheelEvent<HTMLElement>) => {
    const article = articleRef.current;
    if (!article) return;
    e.stopPropagation();
  };

  useEffect(() => {
    setIsBladeHidden(false);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const path = eventPath(event);
      const isDialogClick = path.some((element) => element.matches('div[role="dialog"], div._overlay_8l8f4_1'));

      if (isTableRowTarget(path)) {
        // A row click may arrive while the close animation is still running. The page's
        // handleRowClick runs on the same click; abort the pending unmount and show again.
        reopenBlade();
        return;
      }

      if (bladeRef.current && !path.includes(bladeRef.current) && !isDialogClick) {
        closeBlade();
      }
    };

    // click, not mousedown: row onClick runs in the same gesture before this bubbles up,
    // so selection updates before we decide to close. mousedown closed first and raced
    // open-then-immediately-close on standing-orders row clicks (SPR-1208).
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
      cancelPendingClose();
    };
  }, [cancelPendingClose, closeBlade, reopenBlade]);

  return (
    <div ref={bladeRef} className={`${styles.card} ${isBladeHidden ? styles.hidden : ''}`} style={{ '--blade-width': width } as CSSProperties}>
      <header className={styles.header}>
        <h1 className={`${styles['header-top']}`}>
          {headerTop}
          <button onClick={closeBlade}>
            <img src={close} alt="" />
          </button>
        </h1>
        <div className={styles['header-bottom']}>{headerBottom}</div>
      </header>
      {/* ph-mask: פאנלי הפירוט מציגים את ה-PII העשיר ביותר (שמות, ת"ז, חשבונות בנק) -
          מיסוך כאן מכסה את כולם בהקלטות ה-PostHog (HAT-1081). */}
      <article ref={articleRef} className={`${styles.content} custom-scrollbar ph-mask`} onWheel={handleWheel} tabIndex={-1}>
        {children}
      </article>
    </div>
  );
};

export default Blade;
